import { useEffect, useMemo, useState } from 'react';
import type { RelationFilter } from '@crm/contracts';
import { useEnabledPlugins } from './useEnabledPlugins';
import { subscribeRelationFilters } from './relationFilterBus';

/**
 * Gli id che i plugin abilitati vogliono nascosti in un pannello relazione.
 *
 * Stesso filtraggio degli altri contributor — solo i plugin che l'utente ha
 * davvero — e stessa dottrina: il core non sa perché quegli id vadano nascosti,
 * chiede e applica.
 *
 * ## Un errore non svuota il pannello
 *
 * Se la chiamata di un plugin fallisce, quel filtro vale come «nessuna
 * esclusione» e il pannello mostra tutto. L'alternativa — propagare l'errore —
 * darebbe un riquadro vuoto, che si legge come «non c'è niente collegato»: una
 * risposta plausibile a un'altra domanda, ed è il modo peggiore di sbagliare.
 *
 * ## Perché un Set e non un array
 *
 * Perché chi lo consuma cerca dentro, una volta per riga.
 */
export function useRelationFilters(sourceType: string, targetType: string, sourceId: number) {
  const enabledPlugins = useEnabledPlugins();

  const filtri = useMemo<RelationFilter[]>(
    () =>
      enabledPlugins.flatMap((p) =>
        (p.relationFilters ?? []).filter(
          (f) => f.sourceType === sourceType && f.targetType === targetType,
        ),
      ),
    [enabledPlugins, sourceType, targetType],
  );

  const [nascosti, setNascosti] = useState<Set<number>>(() => new Set());

  // Il plugin possiede i dati, quindi è lui a dire quando sono cambiati: senza
  // questo, scollegare un veterinario non lo fa ricomparire fra i contatti fino
  // al ricaricamento della pagina.
  const [giro, setGiro] = useState(0);
  useEffect(() => subscribeRelationFilters(() => setGiro((n) => n + 1)), []);

  // `chiave` e non `filtri` fra le dipendenze: `flatMap` produce un array nuovo
  // a ogni render anche quando i plugin non cambiano, e l'effetto ripartirebbe
  // in continuazione. È lo stesso inciampo che nel box Veterinari aveva prodotto
  // duemila richieste, tutte 200.
  const chiave = filtri.map((f) => f.key).join(',');

  useEffect(() => {
    if (filtri.length === 0) {
      setNascosti((precedente) => (precedente.size === 0 ? precedente : new Set()));
      return;
    }

    let vivo = true;

    Promise.all(
      filtri.map((f) =>
        f.hiddenTargetIds(sourceId).catch(() => {
          // Vedi il docblock: un filtro rotto non nasconde niente.
          return [] as number[];
        }),
      ),
    ).then((liste) => {
      if (vivo) setNascosti(new Set(liste.flat()));
    });

    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chiave, sourceId, giro]);

  return nascosti;
}
